import React, { useContext, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FaGoogle, FaFacebook, FaLinkedin, FaGithub } from "react-icons/fa";
import Swal from "sweetalert2";
import axios from "axios";
import { ContextProvider } from "../Context/SharedStateContext";
// import "./EpicxLogin.css";

// Images
import log1 from "../assets/img/Login-image.png";
import log2 from "../assets/img/login-img-2.png";
import log3 from "../assets/img/login-security.png";
import log4 from "../assets/img/Review Manage.png";

const EpicxLogin = () => {
  const { productSelected, planTypeSelected, planSelected } = useContext(ContextProvider);
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [epicxFormData, setEpicxFormData] = useState({
    email: "",
    password: ""
  });
  const [epicxErrors, setEpicxErrors] = useState({});
  const [epicxShowPassword, setEpicxShowPassword] = useState(false);
  const [epicxIsSubmitting, setEpicxIsSubmitting] = useState(false);
  const [epicxCurrentImageIndex, setEpicxCurrentImageIndex] = useState(0);
  const [epicxShowForgotPassword, setEpicxShowForgotPassword] = useState(false);
  const [epicxForgotEmail, setEpicxForgotEmail] = useState("");
  const [epicxResetMessage, setEpicxResetMessage] = useState("");

  // Images for carousel
  const epicxImages = [
      { src: log1, alt: "Customer review" },
      { src: log2, alt: "Chat interface" },
      { src: log3, alt: "Login Security" },
      { src: log4, alt: "Review Manage" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setEpicxCurrentImageIndex(prev => (prev + 1) % epicxImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const epicxHandleInputChange = (e) => {
    const { name, value } = e.target;
    setEpicxFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user types
    if (epicxErrors[name]) {
      setEpicxErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const epicxValidateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!epicxFormData.email) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(epicxFormData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!epicxFormData.password) {
      newErrors.password = "Password is required";
    }

    setEpicxErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const epicxHandleLogin = async (e) => {
    e.preventDefault();
    
    if (!epicxValidateForm()) return;
    
    setEpicxIsSubmitting(true);
    
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/mainlogin`,
        { inputvalue: epicxFormData },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "$2a$12$tXgoc0t8Pfva7LmlyQ6/aOtB//vaEWOcdV3ElI82pAlevLYSLeg6W",
          },
        }
      );

      if (response.status === 200) {
        const { tenant_name, tenant_database, site_url, tenant_email, chatWidget } = response.data;
        console.log("Login successful:", response.data);
        
        // Store user data
        localStorage.setItem("tenantName", tenant_name);
        localStorage.setItem("shopURL", site_url);
        localStorage.setItem("tenantEmail", tenant_email);
        localStorage.setItem("isWidget", chatWidget?.isWidget || false);

        Swal.fire({
          icon: "success",
          title: "Login Successful!",
          showConfirmButton: false,
          timer: 1500
        });
        
        navigate(`/${tenant_name}/products`, {
          state: { tenant_name, tenantDB: tenant_database, tenant_email },
        });
      } else {
        throw new Error(response.data.message || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: error.response?.data?.message || "Invalid email or password",
      });
    } finally {
      setEpicxIsSubmitting(false);
    }
  };

  const epicxHandleForgotPassword = async () => {
    if (!epicxForgotEmail) {
      setEpicxResetMessage("Email is required");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(epicxForgotEmail)) {
      setEpicxResetMessage("Please enter a valid email");
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/forgot-password`,
        { email: epicxForgotEmail },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "$2a$12$tXgoc0t8Pfva7LmlyQ6/aOtB//vaEWOcdV3ElI82pAlevLYSLeg6W",
          },
        }
      );

      if (response.status === 200) {
        setEpicxResetMessage("Password reset link sent to your email");
        setTimeout(() => {
          setEpicxShowForgotPassword(false);
          setEpicxResetMessage("");
        }, 3000);
      } else {
        throw new Error(response.data.message || "Failed to send reset link");
      }
    } catch (error) {
      console.error("Forgot password error:", error);
      setEpicxResetMessage(
        error.response?.data?.message || "Error sending reset link. Please try again."
      );
    }
  };

  const epicxHandleSocialLogin = async (provider) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/social-login/${provider}`,
        { 
          registerdata: {
            product: productSelected,
            plan_name: planSelected,
            plan_type: planTypeSelected
          }
        }
      );
      window.location.href = response.data;
    } catch (error) {
      console.error("Social login error:", error);
      Swal.fire({
        icon: "error",
        title: "Login Failed",
        text: "Could not connect to the social provider. Please try again.",
      });
    }
  };

  return (
    <div className="epicx-login-container">
      {/* Left Side - Carousel */}
      <div className="epicx-login-carousel">
        <div className="epicx-carousel-slide">
          <img 
            src={epicxImages[epicxCurrentImageIndex].src} 
            alt={epicxImages[epicxCurrentImageIndex].alt} 
          />
        </div>
        <div className="epicx-carousel-dots">
          {epicxImages.map((_, index) => (
            <button
              key={index}
              className={`epicx-dot ${index === epicxCurrentImageIndex ? "epicx-active" : ""}`}
              onClick={() => setEpicxCurrentImageIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="epicx-login-form-container">
        <div className="epicx-form-header">
          <h2>Welcome Back</h2>
          <p>Login to your EPIC-X account</p>
        </div>

        <form onSubmit={epicxHandleLogin} className="epicx-login-form">
          <div className="epicx-form-group">
            <label htmlFor="epicx-email">Email Address</label>
            <input
              type="email"
              id="epicx-email"
              name="email"
              value={epicxFormData.email}
              onChange={epicxHandleInputChange}
              className={epicxErrors.email ? "epicx-error" : ""}
              placeholder="your@email.com"
            />
            {epicxErrors.email && <span className="epicx-error-message">{epicxErrors.email}</span>}
          </div>

          <div className="epicx-form-group">
  <label htmlFor="epicx-password">Password</label>
  <div className="epicx-password-input">
    <input
      type={epicxShowPassword ? "text" : "password"}
      id="epicx-password"
      name="password"
      value={epicxFormData.password}
      onChange={epicxHandleInputChange}
      className={epicxErrors.password ? "epicx-error" : ""}
      placeholder="Enter your password"
    />
    <button 
      type="button" 
      onClick={() => setEpicxShowPassword(!epicxShowPassword)}
      className="epicx-toggle-password"
      aria-label={epicxShowPassword ? "Hide password" : "Show password"}
    >
      <FontAwesomeIcon icon={epicxShowPassword ? faEyeSlash : faEye} />
    </button>
  </div>
  {epicxErrors.password && <span className="epicx-error-message">{epicxErrors.password}</span>}
</div>


          <div className="epicx-form-options">
            <button 
              type="button" 
              className="epicx-forgot-password"
              onClick={() => setEpicxShowForgotPassword(true)}
            >
              Forgot Password?
            </button>
          </div>

          <button 
            type="submit" 
            className="epicx-submit-button"
            disabled={epicxIsSubmitting}
          >
            {epicxIsSubmitting ? (
              <>
                <span className="epicx-spinner"></span> Logging In...
              </>
            ) : (
              "Log In"
            )}
          </button>
        </form>

        {/* <div className="epicx-social-login">
          <div className="epicx-divider">
            <span>OR</span>
          </div>
          
          <div className="epicx-social-buttons">
            <button 
              onClick={() => epicxHandleSocialLogin("google")}
              aria-label="Login with Google"
            >
              <FaGoogle /> Continue with Google
            </button>
            <button 
              onClick={() => epicxHandleSocialLogin("facebook")}
              aria-label="Login with Facebook"
            >
              <FaFacebook /> Continue with Facebook
            </button>
            <button 
              onClick={() => epicxHandleSocialLogin("linkedin")}
              aria-label="Login with LinkedIn"
            >
              <FaLinkedin /> Continue with LinkedIn
            </button>
            <button 
              onClick={() => epicxHandleSocialLogin("github")}
              aria-label="Login with GitHub"
            >
              <FaGithub /> Continue with GitHub
            </button>
          </div>
        </div> */}

        <div className="epicx-form-footer">
          <p>
            Don't have an account?{" "}
            <Link to="/register" className="epicx-register-link">Sign up</Link>
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {epicxShowForgotPassword && (
        <div className="epicx-modal-overlay">
          <div className="epicx-modal-content">
            <h3>Reset Password</h3>
            <p>Enter your email to receive a password reset link</p>

            <div className="epicx-form-group">
              <input
                type="email"
                placeholder="your@email.com"
                value={epicxForgotEmail}
                onChange={(e) => setEpicxForgotEmail(e.target.value)}
              />
              {epicxResetMessage && (
                <p className={`epicx-reset-message ${
                  epicxResetMessage.includes("sent") ? "epicx-success" : "epicx-error"
                }`}>
                  {epicxResetMessage}
                </p>
              )}
            </div>

            <div className="epicx-modal-actions">
              <button 
                onClick={epicxHandleForgotPassword}
                className="epicx-primary-button"
              >
                Send Reset Link
              </button>
              <button 
                onClick={() => {
                  setEpicxShowForgotPassword(false);
                  setEpicxResetMessage("");
                }}
                className="epicx-secondary-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EpicxLogin;